import React from 'react';
import { useNavigate } from 'react-router-dom';
import useHttp from '../../hooks/http.hook';
import { useBranchNav } from '../../hooks/branchNav.hook';
import '../dashboard/Dashboard.css';
import '../business/Business.css';
import './CategoriesPage.css';
import CreateBusinessHeader from '../business/components/CreateBusinessHeader';
import IconButton from '../dashboard/components/product_item_components/IconButton';
import { EditIcon, TrashIcon } from '../dashboard/components/product_item_components/icons';
import DeleteProductModal from '../dashboard/components/product_item_components/DeleteProductModal';

function CategoriesPage() {
  const navigate = useNavigate();
  const { branchHomePath } = useBranchNav();
  const { loading: httpLoading, requestWithMeta } = useHttp();

  const [categories, setCategories] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [categoryToDelete, setCategoryToDelete] = React.useState(null);
  const [deleteError, setDeleteError] = React.useState(null);

  const fetchCategories = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await requestWithMeta('/operator/categories', 'GET');
      if (!res?.ok) {
        setError(res?.data?.error || res?.data?.message || 'Не удалось загрузить категории');
        setCategories([]);
        return;
      }
      const list = Array.isArray(res?.data?.categories) ? res.data.categories : [];
      setCategories(list);
    } catch (_) {
      setError('Ошибка загрузки');
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, [requestWithMeta]);

  React.useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  React.useEffect(() => {
    if (!categoryToDelete) setDeleteError(null);
  }, [categoryToDelete]);

  const handleBack = () => {
    navigate(branchHomePath);
  };

  const handleCreateCategory = () => {
    navigate('/categories/create');
  };

  const handleOpenProducts = (category) => {
    const id = category?.id ?? category?._id;
    if (id == null) return;
    navigate(`/categories/${encodeURIComponent(id)}/products`, { state: { category } });
  };

  const handleEdit = (e, category) => {
    e.stopPropagation();
    const id = category?.id ?? category?._id;
    if (id == null) return;
    navigate(`/categories/${encodeURIComponent(id)}/edit`, { state: { category } });
  };

  const handleAskDelete = (e, category) => {
    e.stopPropagation();
    setCategoryToDelete(category);
  };

  const handleConfirmDelete = async () => {
    const category = categoryToDelete;
    const id = category?.id ?? category?._id;
    if (id == null) return;

    setDeleteError(null);
    try {
      const res = await requestWithMeta(
        `/operator/categories/${encodeURIComponent(id)}`,
        'DELETE'
      );
      if (!res?.ok) {
        setDeleteError(res?.data?.error || res?.data?.message || 'Не удалось удалить категорию');
        return;
      }
      setCategories((prev) =>
        prev.filter((c) => String(c?.id ?? c?._id) !== String(id))
      );
      setCategoryToDelete(null);
    } catch (_) {
      setDeleteError('Ошибка удаления');
    }
  };

  const deleteName =
    categoryToDelete?.name ??
    (categoryToDelete
      ? `Категория #${categoryToDelete?.id ?? categoryToDelete?._id}`
      : '');

  return (
    <div className="categories-page-root">
      <CreateBusinessHeader
        title="Категории"
        subtitle=""
        onBack={handleBack}
      />

      <main className="categories-page-content">
        {loading ? (
          <div className="categories-page-loading" aria-busy="true">
            <div className="categories-page-skeleton categories-page-skeleton--block" />
            <div className="categories-page-skeleton categories-page-skeleton--block" />
            <div className="categories-page-skeleton categories-page-skeleton--block" />
          </div>
        ) : error ? (
          <div className="categories-page-error">{error}</div>
        ) : (
          <>
            <div className="main-page-sectionHeader categories-page-sectionHeader">
              <button
                type="button"
                className="main-page-sectionSubButton"
                onClick={() => navigate('/categories/index')}
                disabled={categories.length === 0}
              >
                Порядок
              </button>
              <div className="main-page-sectionSub">
                <span>
                  {categories.length
                    ? `${categories.length} ${categories.length === 1 ? 'категория' : categories.length >= 2 && categories.length <= 4 ? 'категории' : 'категорий'}`
                    : 'Нет категорий'}
                </span>
              </div>
            </div>

            {categories.length === 0 ? (
              <div className="categories-page-empty">Нет категорий</div>
            ) : (
              <ul className="categories-page-list" aria-label="Список категорий">
                {categories.map((category) => {
                  const id = category?.id ?? category?._id;
                  const name = category?.name ?? `Категория #${id}`;
                  const productCount =
                    category?.productCount ??
                    (Array.isArray(category?.productIds) ? category.productIds.length : 0);
                  const isDeleting =
                    categoryToDelete != null &&
                    String(categoryToDelete?.id ?? categoryToDelete?._id) === String(id);

                  return (
                    <li
                      key={id}
                      className="categories-page-item"
                      role="button"
                      tabIndex={0}
                      onClick={() => handleOpenProducts(category)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleOpenProducts(category);
                        }
                      }}
                    >
                      <div className="categories-page-itemRow">
                        <div className="categories-page-itemMain">
                          <span className="categories-page-itemName">{name}</span>
                          <span className="categories-page-itemCount">
                            {productCount}{' '}
                            {productCount === 1
                              ? 'товар'
                              : productCount >= 2 && productCount <= 4
                                ? 'товара'
                                : 'товаров'}
                          </span>
                        </div>
                        <div
                          className="categories-page-itemActions main-page-productActions"
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                        >
                          <IconButton
                            onClick={(e) => handleEdit(e, category)}
                            label={`Редактировать «${name}»`}
                            title="Редактировать"
                            disabled={isDeleting && httpLoading}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            onClick={(e) => handleAskDelete(e, category)}
                            label={`Удалить «${name}»`}
                            title="Удалить"
                            disabled={httpLoading}
                            className="main-page-iconButton"
                          >
                            <TrashIcon />
                          </IconButton>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </main>

      <footer className="categories-page-footerRow">
        <button
          type="button"
          className="categories-page-footerButton categories-page-footerButtonPrimary"
          onClick={handleCreateCategory}
        >
          + Добавить категорию
        </button>
      </footer>

      <DeleteProductModal
        isOpen={!!categoryToDelete}
        onClose={() => setCategoryToDelete(null)}
        title="Удалить категорию"
        productName={deleteName}
        deleteError={deleteError}
        loading={httpLoading}
        onConfirmDelete={handleConfirmDelete}
      />
    </div>
  );
}

export default CategoriesPage;
